'use client';

import type { EvaluationContext, FeatureFlagAdapter, FlagEvaluation } from './adapter.types';
import { noMatch } from './adapter.types';

import { FEATURE_FLAGS } from '@/feature-flags/consts/flags.const';

export const E2E_LEVEL_TWO_WINDOW_KEY = '__MEDPREV_E2E_LEVEL_TWO__';

export interface E2ESnapshot {
  config: Record<string, boolean>;
  match: boolean;
  error?: boolean;
}

export type E2ESnapshotsByFlagKey = Partial<Record<string, E2ESnapshot>>;

declare global {
  interface Window {
    [E2E_LEVEL_TWO_WINDOW_KEY]?: E2ESnapshot;
  }
}

export function getE2ESnapshots(): E2ESnapshotsByFlagKey | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const snapshot = window[E2E_LEVEL_TWO_WINDOW_KEY];

  if (!snapshot) {
    return undefined;
  }

  return { [FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO]: snapshot };
}

export function hasE2ESnapshot(): boolean {
  return Boolean(getE2ESnapshots());
}

export class StaticFeatureFlagAdapter implements FeatureFlagAdapter {
  readonly name = 'static';

  private snapshots: E2ESnapshotsByFlagKey;

  constructor(snapshots: E2ESnapshotsByFlagKey) {
    this.snapshots = snapshots;
  }

  async initialize(): Promise<void> {}

  async evaluate(flagKey: string, _context: EvaluationContext): Promise<FlagEvaluation> {
    const snapshot = this.snapshots[flagKey];

    if (!snapshot) {
      return noMatch(flagKey);
    }

    if (snapshot.error) {
      throw new Error('StaticFeatureFlagAdapter: simulated evaluation error');
    }

    if (!snapshot.match) {
      return noMatch(flagKey);
    }

    const hasActiveFeature = Object.values(snapshot.config).some(Boolean);

    return {
      flagKey,
      variant: hasActiveFeature ? 'treatment' : 'control',
      attachment: JSON.stringify(snapshot.config),
      match: true,
    };
  }
}
